import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SectionWave from '../components/SectionWave';
import SEO from '../components/SEO';
import { useEvents } from '../hooks/useEvents';
import './PageShared.css';
import './Events.css';

const PALETTE = [
  '#E8799F', '#7DCFCA', '#F9B98A', '#9B59B6', '#3498DB', '#2ECC71', '#E67E22',
];

function colorForId(id) {
  return PALETTE[id % PALETTE.length];
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function isPast(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr + 'T23:59:59') < new Date();
}

function availableSeats(slot) {
  const seats = slot.event?.seats;
  const signups = slot.event?.signup_count ?? 0;
  if (!seats) return null;
  return Math.max(0, seats - signups);
}

function formatPrice(price) {
  if (!price) return 'Gratis';
  return `${price} kr.`;
}

// Parse URLs in a text string into clickable links, showing only the domain
const URL_RE = /https?:\/\/[^\s]+/g;
function parseLinks(text) {
  const parts = [];
  let last = 0;
  let match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const url = match[0].replace(/[.,)]+$/, ''); // strip trailing punctuation
    let label;
    try { label = new URL(url).hostname.replace(/^www\./, ''); }
    catch { label = url.slice(0, 30) + '…'; }
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="ev-desc-link">
        {label}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Deduplicate slots — keep the earliest slot per event_id
function deduplicateSlots(slots) {
  const seen = new Map();
  for (const slot of slots) {
    const key = slot.event_id;
    if (!seen.has(key) || slot.date < seen.get(key).date) {
      seen.set(key, slot);
    }
  }
  return Array.from(seen.values());
}

function EventModal({ slot, onClose }) {
  const navigate = useNavigate();
  const ev = slot.event;
  const past = isPast(slot.date);
  const avail = availableSeats(slot);
  const color = colorForId(ev?.id ?? 0);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleSignup() {
    onClose();
    navigate('/login');
  }

  const soldOut = avail === 0;

  return (
    <div className="ev-modal-backdrop" onClick={onClose}>
      <div className="ev-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

        {/* Header image or color bar */}
        {ev?.image?.filename ? (
          <div className="ev-modal-hero">
            <img src={`https://api.yogo.dk/uploads/${ev.image.filename}`} alt={ev.name} />
            <div className="ev-modal-hero-overlay" style={{ background: `linear-gradient(to top, ${color}cc 0%, transparent 55%)` }} />
            <button className="ev-modal-close" onClick={onClose} aria-label="Luk">✕</button>
          </div>
        ) : (
          <div className="ev-modal-colorbar" style={{ background: `linear-gradient(135deg, ${color}33, ${color}11)`, borderBottom: `3px solid ${color}` }}>
            <button className="ev-modal-close ev-modal-close-light" onClick={onClose} aria-label="Luk">✕</button>
          </div>
        )}

        <div className="ev-modal-scroll">
          <div className="ev-modal-body">
            {/* Tags */}
            <div className="ev-modal-meta">
              <span className="ev-tag" style={{ color, borderColor: color + '55', background: color + '18' }}>
                {ev?.event_group?.name?.trim() || 'Event'}
              </span>
              {past && <span className="ev-tag ev-tag-past">Afsluttet</span>}
              {!past && soldOut && <span className="ev-tag ev-tag-soldout">Fuldt booket</span>}
            </div>

            <h2>{ev?.name}</h2>

            {/* Detail chips */}
            <div className="ev-modal-chips">
              <div className="ev-chip">
                <span className="ev-chip-icon">📅</span>
                <div>
                  <div className="ev-chip-label">Dato</div>
                  <div className="ev-chip-val">{formatDate(slot.date)}</div>
                </div>
              </div>
              <div className="ev-chip">
                <span className="ev-chip-icon">🕐</span>
                <div>
                  <div className="ev-chip-label">Tidspunkt</div>
                  <div className="ev-chip-val">{slot.start_time} – {slot.end_time}</div>
                </div>
              </div>
              <div className="ev-chip">
                <span className="ev-chip-icon">💰</span>
                <div>
                  <div className="ev-chip-label">Pris</div>
                  <div className="ev-chip-val">{formatPrice(ev?.price)}</div>
                </div>
              </div>
              {ev?.seats && (
                <div className="ev-chip">
                  <span className="ev-chip-icon">👥</span>
                  <div>
                    <div className="ev-chip-label">Pladser</div>
                    <div className="ev-chip-val" style={{ color: soldOut ? '#e74c3c' : avail !== null && avail <= 5 ? '#E8799F' : undefined }}>
                      {avail === null
                        ? `${ev.seats} i alt`
                        : soldOut
                          ? 'Fuldt booket'
                          : `${avail} ledige af ${ev.seats}`}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {ev?.description && (
              <div className="ev-modal-desc">
                {ev.description.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{parseLinks(line)}</p> : <br key={i} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer CTA */}
        {!past && (
          <div className="ev-modal-footer">
            {soldOut ? (
              <button className="btn btn-outline" style={{ width: '100%' }} disabled>Fuldt booket</button>
            ) : (
              <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handleSignup}>
                Tilmeld dig →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EventCard({ slot, onClick }) {
  const ev = slot.event;
  const past = isPast(slot.date);
  const avail = availableSeats(slot);
  const color = colorForId(ev?.id ?? 0);
  const almostFull = avail !== null && avail > 0 && avail <= 5;
  const soldOut = avail === 0;

  return (
    <div
      className={`ev-card${past ? ' ev-card-past' : ''}`}
      style={{ '--ev-color': color }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className="ev-card-img-wrap">
        {ev?.image?.filename ? (
          <img src={`https://api.yogo.dk/uploads/${ev.image.filename}`} alt={ev.name} />
        ) : (
          <div className="ev-card-img-placeholder" style={{ background: `linear-gradient(135deg, ${color}22, ${color}0a)` }}>
            <span style={{ fontSize: '2.5rem' }}>🎉</span>
          </div>
        )}
        <div className="ev-card-img-grad" style={{ background: `linear-gradient(to top, ${color}bb 0%, transparent 60%)` }} />
        {past && <div className="ev-card-img-overlay">Afsluttet</div>}
        <div className="ev-card-badges">
          {soldOut && !past && <span className="ev-badge ev-badge-soldout">Fuldt booket</span>}
          {almostFull && !past && <span className="ev-badge ev-badge-few">Kun {avail} tilbage</span>}
        </div>
      </div>

      <div className="ev-card-body">
        <span className="ev-tag" style={{ color, borderColor: color + '44', background: color + '15' }}>
          {ev?.event_group?.name?.trim() || 'Event'}
        </span>

        <h3>{ev?.name}</h3>

        <div className="ev-card-meta-row">
          <span>📅 {formatDate(slot.date)}</span>
          <span>🕐 {slot.start_time}–{slot.end_time}</span>
        </div>

        <div className="ev-card-bottom">
          <span className="ev-card-price">{formatPrice(ev?.price)}</span>
          <span className="ev-card-cta">
            {past ? 'Se detaljer' : 'Læs mere'} →
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { slots, loading, error } = useEvents();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('upcoming');
  const location = useLocation();

  const today = new Date().toISOString().slice(0, 10);

  // Open slot passed via navigate state (from calendar)
  useEffect(() => {
    const openSlotId = location.state?.openSlot;
    if (openSlotId && slots.length > 0) {
      const slot = slots.find(s => s.id === openSlotId);
      if (slot) {
        setSelected(slot);
        // Switch to correct tab
        if (slot.date < today) setFilter('past');
      }
    }
  }, [location.state, slots]); // eslint-disable-line react-hooks/exhaustive-deps

  const allUpcoming = slots.filter(s => s.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const allPast     = slots.filter(s => s.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const upcoming = deduplicateSlots(allUpcoming);
  const past     = deduplicateSlots(allPast);
  const displayed = filter === 'upcoming' ? upcoming : past;

  return (
    <main className="page">
      <SEO
        path="/events"
        description="Se kommende events og aktiviteter hos Little-Stars i Randers NV. Tilmeld dig sjove oplevelser for børn og familier."
      />

      <div className="page-hero ev-hero">
        <div className="page-hero-bg" style={{ '--c1': '#4ECDC4', '--c2': '#9B59B6' }} />
        <div className="page-hero-content">
          <div className="page-label">Events</div>
          <h1>Sjove oplevelser <span className="gradient-text">for hele familien</span></h1>
          <p>Vi arrangerer løbende events og aktiviteter — tilmeld dig direkte her.</p>
        </div>
      </div>

      <SectionWave color="#f8f9fd" />

      <section className="content-section" style={{ background: '#f8f9fd', paddingBottom: '4rem' }}>
        <div className="container">
          <div className="ev-filter-bar">
            <button
              className={`ev-filter-btn${filter === 'upcoming' ? ' active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Kommende {!loading && <span className="ev-filter-count">{upcoming.length}</span>}
            </button>
            <button
              className={`ev-filter-btn${filter === 'past' ? ' active' : ''}`}
              onClick={() => setFilter('past')}
            >
              Tidligere {!loading && <span className="ev-filter-count">{past.length}</span>}
            </button>
          </div>

          {loading && (
            <div className="ev-loading">
              <div className="ev-spinner" />
              <p>Henter events…</p>
            </div>
          )}

          {error && (
            <div className="ev-error">
              <p>⚠️ Kunne ikke hente events. Prøv igen lidt senere.</p>
            </div>
          )}

          {!loading && !error && displayed.length === 0 && (
            <div className="ev-empty">
              <p>
                {filter === 'upcoming'
                  ? 'Ingen kommende events lige nu — tjek igen snart!'
                  : 'Ingen tidligere events at vise.'}
              </p>
            </div>
          )}

          {!loading && !error && displayed.length > 0 && (
            <div className="ev-grid">
              {displayed.map(slot => (
                <EventCard key={slot.id} slot={slot} onClick={() => setSelected(slot)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <SectionWave color="#fff" flip />

      <section className="content-section" style={{ background: '#fff' }}>
        <div className="container">
          <div className="private-event-banner">
            <div>
              <div className="section-label">Private events</div>
              <h2 className="section-title">Lej os til din næste <span className="gradient-text">fest</span></h2>
              <p style={{ color: '#5a5a7a', lineHeight: 1.7, marginTop: '0.5rem' }}>
                Vi tilbyder lokaleudlejning til børnefødselsdage, fester og private arrangementer.
                Tag kontakt for at høre om ledige datoer.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <a href="mailto:booking@little-stars.dk" className="btn btn-primary btn-lg">Book et event</a>
            </div>
          </div>
        </div>
      </section>

      {selected && <EventModal slot={selected} onClose={() => setSelected(null)} />}
    </main>
  );
}
